import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-hot-toast";
import { useQueryClient } from "react-query";
import { usePostConfigurarBuscaEmail } from "hooks/groupPay/usePostConfigurarBuscaEmail";
import { TestStatusPayload, useGetStatusTesteEmail } from "hooks/groupPay/useGetStatusTesteEmail";
import AlertBanner, { AlertBannerProps } from "shared/components/alertBanner/AlertBanner";
import { EnumTipoConfiguracaoEmail } from "constants/EnumTipoConfiguracaoEmail";
import { SelectOptionType } from "types/select/SelectOptionType";
import { ConfiguracaoEmailResponse } from "types/response/groupPay/ConfiguracaoEmailResponse";
import { ConfigurarAcessoDiretoSchema, EMAIL_VALIDO_REGEX } from "./schemas/ConfigurarAcessoDiretoSchema";
import { useStyles } from "./styles/ConfigurarAcessoDiretoStyles";
import { Utils } from "./utils/utils";
import { opcoesCriptografia, opcoesProtocolo } from "./utils/constants";
import FormularioAcessoDireto from "./components/FormularioAcessoDireto";
import { TipoGuia, useGetGuiaRedirecionamento } from "hooks/groupPay/useGetGuiaRedirecionamento";
import { NotificationProps } from "shared/components/notification/Notification";
import { useGetConfiguracaoEmail } from "hooks/groupPay/useGetConfiguracaoEmail";

interface ConfigurarAcessoDiretoProps {
  handleNotification: (result: NotificationProps) => void;
  isInsideWizard?: boolean;
  initialData: ConfiguracaoEmailResponse | null;
  onConfiguracaoSalva?: (isConfigurado: boolean) => void;
  setShowModalTermoAceite: (show: boolean) => void;
}

export interface ConfigurarAcessoDiretoForm {
  email: string;
  senha: string;
  servidor: string;
  porta: number;
  tipoProtocolo: SelectOptionType<string>;
  tipoCriptografia: SelectOptionType<string>;
  aceiteTermos: boolean;
}

const ConfigurarAcessoDireto: React.FC<ConfigurarAcessoDiretoProps> = ({
  handleNotification,
  isInsideWizard,
  initialData,
  onConfiguracaoSalva,
  setShowModalTermoAceite,
}) => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const { data: configuracaoData } = useGetConfiguracaoEmail();
  const [alert, setAlert] = useState<AlertBannerProps>({ show: false });
  const [testFailures, setTestFailures] = useState(0);
  const [configuracaoFoiSalva, setConfiguracaoFoiSalva] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  const {
    control,
    handleSubmit,
    register,
    setValue,
    reset: resetForm,
    formState: { dirtyFields, isValid },
  } = useForm<ConfigurarAcessoDiretoForm>({
    resolver: yupResolver(ConfigurarAcessoDiretoSchema),
    mode: "onChange",
    defaultValues: {
      email: "",
      senha: "",
      servidor: "",
      porta: 0,
      aceiteTermos: false,
    },
  });

  useEffect(() => {
    const habilitarBotaoFinalizar = isValid && configuracaoFoiSalva;
    if (onConfiguracaoSalva) {
      onConfiguracaoSalva(habilitarBotaoFinalizar);
    }
  }, [isValid, configuracaoFoiSalva, onConfiguracaoSalva]);

  const { mutate: salvarConfiguracao } = usePostConfigurarBuscaEmail({
    onSuccess: () => {
      setTestFailures(0);
      setConfiguracaoFoiSalva(true);
      testarRegra();
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || "Ocorreu uma falha ao salvar.";
      toast.error(errorMessage);
      setIsTesting(false);
    },
  });

  const { mutate: getGuia } = useGetGuiaRedirecionamento();

  const mensagemErroCredenciaisInvalidas =
    "Não foi possível autenticar o acesso com os dados informados. Verifique o e-mail e a senha e tente novamente.";

  const isErroCredenciais = (mensagem?: string | null): boolean => {
    const m = (mensagem || "").toLowerCase();
    return (
      /authenticationfailedexception/i.test(m) ||
      /authentication failed/i.test(m) ||
      /falha de autenticação/i.test(m) ||
      /credenciais inválidas/i.test(m)
    );
  };

  const { testar: testarRegra } = useGetStatusTesteEmail({
    onSuccess: (data) => {
      const status = data?.payload?.status;
      const mensagem = data?.payload?.mensagem;
      if (status === "CONCLUIDO") {
        mostrarMensagemSucesso();
      } else if (status === "PENDENTE") {
        if (data?.payload && isTestePendente(data.payload)) {
          mostrarMensagemPendente();
        } else {
          mostrarMensagensErro();
        }
      } else if (status === "ERRO" || status == null) {
        if (isErroCredenciais(mensagem)) {
          mostrarMensagemErroCredenciais();
        } else {
          mostrarMensagensErro();
        }
      }
    },
    onError: () => {
      mostrarMensagensErro();
    },
  });

  const mostrarMensagemSucesso = () => {
    setTestFailures(0);
    setIsTesting(false);
    handleNotification({ show: true, message: "Acesso ao e-mail validado", type: "success" });
    
    const emailAtual = configuracaoData?.payload?.email || initialData?.email;
    if (emailAtual) {
      queryClient.invalidateQueries(['logsDeAcessoEmail', emailAtual]);
    }
  };

  const mostrarMensagensErro = () => {
    setTestFailures((prev) => prev + 1);
    setIsTesting(false);
    handleNotification({
      show: true,
      type: "error",
      message: "Erro no acesso ao e-mail",
    });
    setAlert({
      show: true,
      message: (
        <>
          Erro ao acessar o e-mail da administradora. Verifique e <strong>corrija os dados para nova validação</strong>.
        </>
      ),
    });
  };

  const mostrarMensagemErroCredenciais = () => {
    setTestFailures((prev) => prev + 1);
    setIsTesting(false);
    handleNotification({
      show: true,
      type: "error",
      message: "Erro no acesso ao e-mail",
    });
    setAlert({
      show: true,
      message: mensagemErroCredenciaisInvalidas,
    });
  };

  const mostrarMensagemPendente = () => {
    setTestFailures(0);
    setIsTesting(true);
    handleNotification({
      show: true,
      message: "Aguarde, estamos validando o acesso ao e-mail",
      type: "warning",
    });
  };

  const isTestePendente = (data: TestStatusPayload, limiteMinutos: number = 10): boolean => {
    if (data.status !== "PENDENTE") {
      return false;
    }

    const dataHoraTeste = data.dataHoraTeste ? new Date(data.dataHoraTeste) : new Date();

    const diferencaMilisegundos = new Date().getTime() - dataHoraTeste.getTime();
    const diferencaMinutos = diferencaMilisegundos / 1000 / 60;

    return diferencaMinutos < limiteMinutos;
  };

  useEffect(() => {
    if (initialData) {
      fillForm(initialData);
      testarRegra();
    }
    setTestFailures(0);
  }, [initialData]);

  useEffect(() => {
    if (testFailures >= 3) {
      const alertMessage = (
        <>
          Está com dificuldades para configurar os dados para acesso a sua caixa de e-mail? Acesse nosso{" "}
          <span className={classes.link} onClick={handleSigaOsPassosClick}>
            passo a passo
          </span>{" "}
          ou fale com a nossa{" "}
          <span className={classes.link} onClick={redirectWhatsappSuporte}>
            equipe de suporte
          </span>
          .
        </>
      );
      setAlert({ show: true, message: alertMessage });
    }
  }, [testFailures]);

  const fillForm = (data: ConfiguracaoEmailResponse) => {
    const protocolo = opcoesProtocolo.find((op) => op.value === data.tipoProtocolo);
    const criptografia = opcoesCriptografia.find((op) => op.value === data.tipoCriptografia);
    resetForm({
      email: data.email || "",
      senha: "********",
      servidor: data.servidor || "",
      porta: data.porta || 0,
      tipoProtocolo: protocolo,
      tipoCriptografia: criptografia,
      aceiteTermos: true,
    });
  };

  const onSubmit = (data: ConfigurarAcessoDiretoForm) => {
    setAlert({ show: false });

    const emailTrimmed = (data.email || "").trim();
    if (!EMAIL_VALIDO_REGEX.test(emailTrimmed)) {
      toast.error("Informe um e-mail válido.");
      return;
    }

    const senha = dirtyFields.senha ? data.senha : initialData?.senha || "";
    const isSenhaCriptografada = dirtyFields.senha ? false : true;

    const payload = {
      tipoConfiguracaoEmail: EnumTipoConfiguracaoEmail.ACESSO_DIRETO_CAIXA,
      email: data.email,
      senha: senha,
      servidor: data.servidor,
      porta: data.porta,
      tipoProtocolo: data.tipoProtocolo?.value || "",
      tipoCriptografia: data.tipoCriptografia?.value || "",
      isSenhaCriptografada: isSenhaCriptografada,
    };
    salvarConfiguracao(payload);
  };

  const handleSigaOsPassosClick = () => {
    getGuia({ tipoGuia: TipoGuia.REDIRECIONAMENTO_EMAIL_ADMINISTRADORA });
  };

  const redirectWhatsappSuporte = () => {
    Utils.redirectToWhatsApp();
  };

  return (
    <>
      <div className={classes.container}>
        <p className={classes.description}>
          O Group Pay vai acessar a caixa de e-mail da administradora para buscar as notas fiscais e exibi-las na tela
          de listagem. Para isso, é necessário preencher os campos abaixo.
        </p>

        <p className={classes.helpLinkContainer}>
          Informe os dados de acesso e{" "}
          <span onClick={handleSigaOsPassosClick} className={classes.link}>
            siga os passos aqui
          </span>
          .
        </p>

        <FormularioAcessoDireto
          classes={classes}
          control={control}
          register={register}
          setValue={setValue}
          opcoesProtocolo={opcoesProtocolo}
          opcoesCriptografia={opcoesCriptografia}
          onSubmit={handleSubmit(onSubmit)}
          onShowTermos={() => setShowModalTermoAceite(true)}
          isTesting={isTesting}
        />

        <div className={classes.alertContainer}>
          <AlertBanner show={alert.show} message={alert.message} />
        </div>
      </div>

    </>
  );
};

export default ConfigurarAcessoDireto;
